import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideImageUp } from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { AuthenticatedImageComponent } from '../../../../../authenticated-image/authenticated-image.component';
import {
  DesignTemplate,
  VoucherDesignService,
  VoucherType,
  ptToMm,
} from '../../../../../../services/voucher-design.service';

/** The bundled templates of one voucher type, with a preview of the demo voucher on each. */
@Component({
  selector: 'app-template-picker',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmSkeletonImports,
    HlmTypographyImports,
    AuthenticatedImageComponent,
  ],
  providers: [provideIcons({ lucideImageUp })],
  templateUrl: './template-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplatePickerComponent {
  private readonly designService = inject(VoucherDesignService);

  readonly templates = input<DesignTemplate[] | null>(null);
  readonly voucherType = input<VoucherType>('money');
  /** Off when only the artwork matters (a background for an existing design). */
  readonly filterByType = input(true);
  readonly selectedKey = input<string | null>(null);
  /** Previews are only fetched while the picker is shown. */
  readonly showPreviews = input(true);
  /** Show the artwork alone (a background choice), not the demo voucher on it. */
  readonly artworkOnly = input(false);

  readonly select = output<DesignTemplate>();
  /** The user wants to upload logo and banner for a branded template. */
  readonly uploadBranding = output<void>();

  readonly shown = computed(
    () =>
      this.templates()?.filter(
        (t) => !this.filterByType() || t.voucher_type === this.voucherType(),
      ) ?? null,
  );

  /** A shown template needs logo and banner the company has not uploaded yet. */
  readonly missingBranding = computed(
    () => this.shown()?.some((t) => t.needs_branding && !t.available) ?? false,
  );

  previewUrl(template: DesignTemplate): string {
    return this.artworkOnly()
      ? this.designService.templateArtworkUrlByKey(template.key)
      : this.designService.templatePreviewUrl(template);
  }

  sizeLabel(template: DesignTemplate): string {
    return `${ptToMm(template.page_width_pt)} × ${ptToMm(template.page_height_pt)} mm`;
  }
}
